/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { useState, useEffect, useCallback, type Dispatch, type SetStateAction } from 'react';
import {
  AIGoal,
  NewsItem,
  Scenario,
  CityStats,
  Grid,
  ResearchState,
  GameStatus,
  Milestone,
} from '../types';
import { gameStore } from '../stores/gameStore';
import { resolvePlayAudio } from '../src/adapters/ui/hooks/useAudio';
import { architectureFeatures } from '../src/infrastructure/config/features';
import {
  saveGame as persistGame,
  loadGame as restoreGame,
  hasSave as checkHasSave,
} from '../src/adapters/ui/hooks/useGamePersistence';

const SAVE_KEY = 'compliance_command_save_v1';
const AI_ENABLED_KEY = 'compliance_command_ai_enabled';

function readStoredAiEnabled(): boolean | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(AI_ENABLED_KEY);
    if (raw === null) return null;
    return raw === 'true';
  } catch {
    return null;
  }
}

function persistAiEnabledPreference(enabled: boolean): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(AI_ENABLED_KEY, String(enabled));
  } catch {
    /* ignore quota / private mode */
  }
}

function resolveAiEnabledPreference(data: { aiEnabled?: unknown }): boolean {
  if (typeof data.aiEnabled === 'boolean') return data.aiEnabled;
  const stored = readStoredAiEnabled();
  if (stored !== null) return stored;
  return true;
}

export interface UseGamePersistenceAppDeps {
  newsFeed: NewsItem[];
  setNewsFeed: Dispatch<SetStateAction<NewsItem[]>>;
  currentGoal: AIGoal | null;
  setCurrentGoal: Dispatch<SetStateAction<AIGoal | null>>;
  statsDay: number;
  activeScenario: Scenario | null;
  gameStarted: boolean;
  gameStatus: GameStatus;
  setStats: (stats: CityStats | ((prev: CityStats) => CityStats)) => void;
  setResearch: (research: ResearchState | ((prev: ResearchState) => ResearchState)) => void;
  setGrid: (grid: Grid | ((prev: Grid) => Grid)) => void;
  setMilestones: (milestones: Milestone[] | ((prev: Milestone[]) => Milestone[])) => void;
  setGameStatus: (status: GameStatus) => void;
  setGameStarted: (value: boolean) => void;
  setAiEnabled: (value: boolean) => void;
  triggerStory: (customText?: string) => Promise<void>;
  addNewsItem: (item: NewsItem) => void;
}

export function useGamePersistenceApp({
  newsFeed,
  setNewsFeed,
  currentGoal,
  setCurrentGoal,
  statsDay,
  activeScenario,
  gameStarted,
  gameStatus,
  setStats,
  setResearch,
  setGrid,
  setMilestones,
  setGameStatus,
  setGameStarted,
  setAiEnabled,
  triggerStory,
  addNewsItem,
}: UseGamePersistenceAppDeps) {
  const [savedGameAvailable, setSavedGameAvailable] = useState(() =>
    architectureFeatures.useRepositoryPersistence
      ? checkHasSave()
      : !!localStorage.getItem(SAVE_KEY)
  );

  const refreshSavedGameFlag = useCallback(() => {
    setSavedGameAvailable(
      architectureFeatures.useRepositoryPersistence
        ? checkHasSave()
        : !!localStorage.getItem(SAVE_KEY)
    );
  }, []);

  useEffect(() => {
    if (!gameStarted) {
      refreshSavedGameFlag();
    }
  }, [gameStarted, refreshSavedGameFlag]);

  const buildSaveState = useCallback(() => {
    const snap = gameStore.getState();
    const aiEnabled = snap.aiEnabled;
    persistAiEnabledPreference(aiEnabled);
    return {
      stats: snap.stats,
      research: snap.research,
      grid: snap.grid,
      milestones: snap.milestones,
      gameStatus: snap.gameStatus,
      newsFeed: newsFeed.slice(-20),
      currentGoal,
      aiEnabled,
      timestamp: Date.now(),
      savedAt: new Date().toISOString(),
    };
  }, [newsFeed, currentGoal]);

  const saveGame = useCallback(async () => {
    if (activeScenario) return;
    const stateToSave = buildSaveState();
    try {
      if (architectureFeatures.useRepositoryPersistence) {
        await persistGame(stateToSave);
      } else {
        localStorage.setItem(SAVE_KEY, JSON.stringify(stateToSave));
      }
      setSavedGameAvailable(true);
    } catch (e) {
      console.warn('Failed to save game', e);
    }
  }, [activeScenario, buildSaveState]);

  useEffect(() => {
    if (gameStarted && gameStatus === 'playing' && !activeScenario) {
      const tick = gameStore.getState().tickNumber;
      if (tick > 0 && (tick === 1 || tick % 5 === 0)) {
        void saveGame();
      }
    }
  }, [statsDay, gameStarted, gameStatus, activeScenario, saveGame]);

  useEffect(() => {
    const isSandboxActive = () => gameStarted && !activeScenario;

    const syncLocalSave = () => {
      if (!isSandboxActive()) return;
      try {
        localStorage.setItem(SAVE_KEY, JSON.stringify(buildSaveState()));
      } catch (e) {
        console.warn('Failed to sync save on exit', e);
      }
    };

    const handlePageHide = () => {
      if (!architectureFeatures.useRepositoryPersistence) {
        syncLocalSave();
      }
    };

    const handleBeforeUnload = () => {
      if (!architectureFeatures.useRepositoryPersistence) {
        syncLocalSave();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'hidden' || !isSandboxActive()) return;
      if (architectureFeatures.useRepositoryPersistence) {
        void saveGame();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [gameStarted, activeScenario, buildSaveState, saveGame]);

  const loadGame = useCallback(async (): Promise<boolean> => {
    try {
      const data = architectureFeatures.useRepositoryPersistence
        ? await restoreGame()
        : (() => {
            const saved = localStorage.getItem(SAVE_KEY);
            return saved ? JSON.parse(saved) : null;
          })();
      if (data) {
        setStats(data.stats);
        if (data.research) setResearch(data.research);
        setGrid(data.grid);
        setMilestones(data.milestones);
        setGameStatus(data.gameStatus);
        const recoveryReason =
          typeof data.loadRecoveryReason === 'string' ? data.loadRecoveryReason : null;
        const restoredFeed = Array.isArray(data.newsFeed) ? data.newsFeed : [];
        setNewsFeed(
          recoveryReason
            ? [
                ...restoredFeed,
                {
                  id: `recovery_${Date.now()}`,
                  text: recoveryReason,
                  type: 'negative' as const,
                },
              ]
            : restoredFeed,
        );
        setCurrentGoal(data.currentGoal);
        gameStore.getState().setTickNumber(data.stats?.day ?? 0);
        gameStore.getState().setGameSpeed(0);
        setGameStarted(true);
        setAiEnabled(resolveAiEnabledPreference(data));
        triggerStory(
          recoveryReason
            ? 'System State Restored with repairs. Review the news feed for details.'
            : 'System State Restored. Resume Operations.',
        );
        resolvePlayAudio({ type: 'start_ambient' });
        return true;
      }
      return false;
    } catch (e) {
      console.error('Failed to load save', e);
      return false;
    }
  }, [
    setStats,
    setResearch,
    setGrid,
    setMilestones,
    setGameStatus,
    setNewsFeed,
    setCurrentGoal,
    setGameStarted,
    setAiEnabled,
    triggerStory,
  ]);

  const hasSave = useCallback(() => savedGameAvailable, [savedGameAvailable]);

  const handleSaveNow = useCallback(async () => {
    if (!gameStarted || activeScenario) {
      addNewsItem({
        id: Date.now().toString(),
        text: 'Only sandbox simulations can be saved. Exit scenario to save.',
        type: 'neutral',
      });
      return;
    }
    await saveGame();
    addNewsItem({
      id: Date.now().toString(),
      text: 'Simulation checkpoint saved.',
      type: 'positive',
    });
    resolvePlayAudio({ type: 'sfx', sound: 'success' });
  }, [gameStarted, activeScenario, saveGame, addNewsItem]);

  return {
    savedGameAvailable,
    refreshSavedGameFlag,
    saveGame,
    loadGame,
    hasSave,
    handleSaveNow,
  };
}